import {
  LIVE_STATE_VIEW,
  RESTART_ONLY_SERVICE_PATTERNS,
  SYSTEM_SERVICE_PATTERNS,
} from '../config/ServiceManager.config';
import type {
  ActionLabelConfig,
  CommandType,
  LiveStateView,
  RawService,
  ServiceAction,
  ServiceRecord,
  ServiceState,
  ServiceStatus,
} from '../types/ServiceManager.types';
import type { ToggleTarget, WorkplaceMode, WorkplaceService } from '../types/Deployments.types';

/**
 * Convert PM2 state to UI status
 */
export const stateToStatus = (state: ServiceState): ServiceStatus => {
  switch (state) {
    case 'online':
      return 'Running';
    case 'stopped':
      return 'Stopped';
    default:
      return 'Unknown';
  }
};

/**
 * Get expected status after an action
 */
export const getStatusFromAction = (action: ServiceAction): ServiceStatus => {
  switch (action) {
    case 'start':
      return 'Running';
    case 'stop':
      return 'Stopped';
    case 'restart':
      return 'Restarting';
    default:
      return 'Unknown';
  }
};

/**
 * Format bytes to human-readable MB string
 */
export const formatBytes = (value: number | undefined): string => {
  if (typeof value !== 'number') return 'N/A';
  return `${(value / (1024 * 1024)).toFixed(2)}MB`;
};

/**
 * Format milliseconds to duration string
 */
const formatDuration = (ms: number): string => {
  if (!ms || typeof ms !== 'number') return 'N/A';

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${days}d ${hours}h ${minutes}m ${seconds}s`;
};

/**
 * Format uptime from start timestamp
 */
const formatUptime = (startTimestampMs: number | undefined): string => {
  if (!startTimestampMs || typeof startTimestampMs !== 'number') return 'N/A';

  const now = Date.now();
  const diff = now - startTimestampMs;
  if (diff < 0) return 'N/A';
  return formatDuration(diff);
};

/**
 * Map raw service from API to ServiceRecord.
 *
 * `scope` falls back to `'workplace'` when the backend hasn't shipped the
 * field yet — that way old responses still render in the primary table
 * exactly like they did before this fix. Once the backend filters by token
 * and tags rows, the split into workplace / global happens naturally.
 */
export const mapServiceRecord = (service: RawService): ServiceRecord => ({
  id: service.pm_id,
  name: service.name,
  status: stateToStatus(service.state),
  uptime: formatUptime(service.uptime),
  created: 'N/A',
  manualRestarts: service.manual_restarts ?? 0,
  unstableRestart: service.unstable_restarts ?? 0,
  cpu: service.cpu ?? 0,
  memory: formatBytes(service.memory),
  locked: service.locked ?? false,
  hidden: service.hidden ?? false,
  workplaceId: service.workplace_id ?? null,
  scope: service.scope ?? 'workplace',
  // A supervisor that predates the field omits it, and the safe reading of
  // "absent" is the behaviour every service had then.
  control: service.control === 'observe' ? 'observe' : 'managed',
});

/**
 * Check if a service can be modified (not a system service)
 */
export const canBeModified = (service: ServiceRecord): boolean => {
  if (service?.locked) return false;
  // Observe-only: the supervisor answers 403 to start and stop for these, so
  // showing the buttons would be offering a control the server has already
  // declined. This comes from the server rather than from a name pattern,
  // because a pattern stops being true the day something is renamed.
  if (service?.control === 'observe') return false;
  const name = service?.name || '';
  return !SYSTEM_SERVICE_PATTERNS.some((pattern) => name.includes(pattern));
};

/**
 * Restart-only: protected from start, stop and remove, but still restartable.
 *
 * Restart is the one command that is safe on a process we do not own. It acts
 * on something already running, so it can neither resurrect a service somebody
 * deliberately stopped nor start a duplicate.
 */
export const isRestartOnlyService = (service: ServiceRecord): boolean => {
  if (service?.control === 'observe') return true;
  const name = service?.name || '';
  return RESTART_ONLY_SERVICE_PATTERNS.some((pattern) => name.includes(pattern));
};

/** Locked services are never restartable; everything else fully managed or
 *  restart-only is. */
export const canRestart = (service: ServiceRecord): boolean => {
  if (service?.locked) return false;
  return canBeModified(service) || isRestartOnlyService(service);
};

/**
 * Get Bootstrap variant for command badge
 */
export const getCommandVariant = (command: CommandType | string): ActionLabelConfig['variant'] => {
  if (!command) return 'secondary';
  // ORDER IS LOAD-BEARING: "restart".includes("start") is true, so the restart
  // test must come first. It used to come last, which made this branch
  // unreachable and painted every restart row with the green success badge.
  if (command.includes('restart')) return 'warning';
  if (command.includes('stop')) return 'danger';
  if (command.includes('start')) return 'success';
  return 'secondary';
};

/**
 * Filter services by search term
 */
export const filterServicesBySearch = (
  services: ServiceRecord[],
  search: string,
): ServiceRecord[] => {
  if (!search) return services;
  const lowerSearch = search.toLowerCase();
  return services.filter((service) => service.name.toLowerCase().includes(lowerSearch));
};

// Service enablement: telling configured intent apart from live PM2 ---------
//
// Two axes live on every catalog row and they must never be merged:
//   effective        configured intent. What this deployment is set up to run.
//   state / running  live PM2. What is actually up right now.
//
// A green switch drawn over a crash-loop is the failure these helpers exist to
// prevent.

/** Row shape shared by workplace and global catalog services. */
type CatalogRow = Pick<WorkplaceService, 'effective' | 'state' | 'running'> & {
  name?: string;
  envFlag?: string | null;
};

/**
 * Is the live column entitled to say anything at all?
 *
 * `state` is authoritative; `running` is a convenience the supervisor derives
 * from it. Unknown means no `state`, or the literal "unknown" - PM2 was not
 * reached, so nothing in the live column is a fact, including `running: false`.
 */
export const isLiveStateKnown = (row: Pick<CatalogRow, 'state'>): boolean =>
  typeof row?.state === 'string' && row.state !== '' && row.state !== 'unknown';

/**
 * PM2 states that ARE a statement that the process is not up. `launching`, and
 * any word a newer PM2 invents, are deliberately absent: neither is evidence
 * of a stopped process.
 */
const NOT_RUNNING_STATES = new Set(['stopped', 'errored', 'missing']);

/**
 * The live column says this process is not up, and only when it is entitled to
 * say so.
 *
 * Never derived from `running !== true`: `undefined` is not `false`, and an
 * older supervisor that sends no `running` has not told us the process is down.
 */
export const isLiveNotRunning = (row: Pick<CatalogRow, 'state' | 'running'>): boolean => {
  if (!isLiveStateKnown(row)) return false;
  if (row.running === false) return true;
  return NOT_RUNNING_STATES.has(String(row.state).toLowerCase());
};

/**
 * Enabled and demonstrably not up: what a service missing required env looks
 * like. This is the case the Deployments tab must make impossible to miss.
 *
 * An unknown live state is excluded on purpose. PM2 was never reached, so "not
 * running" is not a fact we hold, and claiming it would be the same lie in the
 * opposite direction.
 */
export const isEnabledButNotRunning = (row: CatalogRow): boolean =>
  row?.effective === true && isLiveNotRunning(row);

/**
 * Configured off but a process is still up. Usually a hand-started copy that
 * the supervisor has not reaped yet; it will be stopped within `reapWithinMs`.
 */
export const isDisabledButRunning = (row: CatalogRow): boolean =>
  row?.effective === false && isLiveStateKnown(row) && row.running === true;

/**
 * The refusal the endpoint would answer anyway, predicted client-side.
 *
 * A null `envFlag` makes the supervisor answer 400. That is a property of the
 * service rather than a failed action, so the switch renders disabled with this
 * sentence instead of being clickable and returning an error.
 */
export const toggleBlockedReason = (row: Pick<CatalogRow, 'name' | 'envFlag'>): string | null =>
  row?.envFlag == null
    ? `${row?.name ?? 'This service'} has no service flag, so there is nothing to switch.`
    : null;

/**
 * Stable per-row identity for busy and outcome bookkeeping. Uses the workplace
 * index (the path segment), so two deployments running the same service never
 * collide.
 */
export const toggleRowKey = (target: ToggleTarget): string =>
  target.scope === 'global' ? `global:${target.name}` : `wp:${target.wpIndex}:${target.name}`;

/**
 * How the live column reads. Falls back to the supervisor's own word for a PM2
 * state we have no phrasing for: an unrecognised state must render, never blank
 * the column.
 */
export const liveStateView = (row: Pick<CatalogRow, 'state' | 'running'>): LiveStateView => {
  if (!isLiveStateKnown(row)) return LIVE_STATE_VIEW.unknown;
  const state = String(row.state);
  if (LIVE_STATE_VIEW[state]) return LIVE_STATE_VIEW[state];
  return {
    label: state,
    hint: `PM2 reports state "${state}".`,
    variant: row.running ? 'success' : 'secondary',
  };
};

/** "10 s" / "0.5 s" / "immediately" - the numbers a confirmation quotes. */
export const formatDelay = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return 'immediately';
  return ms % 1000 === 0 ? `${ms / 1000} s` : `${(ms / 1000).toFixed(1)} s`;
};

/**
 * What a deployment's supervisor mode means for turning its services on.
 *
 * The flag write itself always succeeds and is durable, but whether the
 * supervisor then acts on it depends entirely on the mode. From the
 * supervisor's own table:
 *
 *   disabled    the deployment is unknown to it. No rows, no PM2 at all.
 *   observe     it seeds rows and reads status, but never starts or stops.
 *   standalone  it enforces desired state on processes already in PM2, but
 *               never spawns new ones.
 *   managed     full control. This is the only mode where turning a service
 *               on actually starts it.
 *
 * Returns null for `managed`, where there is nothing to warn about.
 */
export const deploymentModeCaveat = (mode: WorkplaceMode): string | null => {
  switch (mode) {
    case 'disabled':
      return (
        'This deployment is disabled, so the supervisor ignores it entirely. ' +
        'Changes here are recorded and take effect if it is set to managed.'
      );
    case 'observe':
      return (
        'This deployment is in observe mode. The supervisor reports on its services ' +
        'but never starts or stops them, so turning one on records the setting ' +
        'without starting the process.'
      );
    case 'standalone':
      return (
        'This deployment is standalone. The supervisor manages processes that are ' +
        'already running but never spawns new ones, so turning on a service that ' +
        'is not already in PM2 will not start it.'
      );
    default:
      return null;
  }
};
